import { useEffect, useMemo, useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { api } from '../api/client';
import { fmtMoisFr } from '../utils/format';
import { ContenuPanier, ContratPrestation, Prestation } from '../types';

/**
 * Panier de prestations d'une ligne de contrat.
 *
 * Ce que recouvre une « mesure » (CCC, CTT) ou une prestation tarifée (OAI)
 * dérive au fil des ans — c'est ce qui rendait les comparaisons entre années
 * impossibles. Le panier est donc historié : chaque changement dit qu'à partir
 * d'un mois donné une prestation entre ou sort. La prestation, elle, ne bouge
 * pas : c'est l'unité stable sur laquelle s'imputent les charges et les
 * salaires (module RH).
 *
 * Le sélecteur de mois permet de relire le panier tel qu'il était à une date
 * passée — ou tel qu'il sera après un changement déjà décidé.
 */
export function PanierPrestationsModal({ ligne, contratLibelle, prestations, onFermer, onModifie }: {
  ligne: ContratPrestation;
  contratLibelle: string;
  prestations: Prestation[];
  onFermer: () => void;
  onModifie: () => void;
}) {
  const maintenant = new Date();
  const moisCourant = `${maintenant.getFullYear()}-${String(maintenant.getMonth() + 1).padStart(2, '0')}`;
  const [moisLecture, setMoisLecture] = useState(moisCourant);
  const [moisEffet, setMoisEffet] = useState(`${maintenant.getFullYear()}-01`);
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer]);

  /** Panier en vigueur au mois donné : dernier événement <= mois, par prestation. */
  const panierAu = (mois: string): Map<number, ContenuPanier> => {
    const dernier = new Map<number, ContenuPanier>();
    const tries = [...ligne.contenu].sort((a, b) => a.mois_effet.localeCompare(b.mois_effet));
    for (const e of tries) {
      if (e.mois_effet.slice(0, 7) <= mois) dernier.set(e.prestation_id, e);
    }
    return dernier;
  };

  const panierLecture = useMemo(() => panierAu(moisLecture), [ligne.contenu, moisLecture]);
  const inclusesLecture = useMemo(
    () => new Set([...panierLecture.values()].filter((e) => e.inclus).map((e) => e.prestation_id)),
    [panierLecture]
  );

  // Le panier à modifier est celui en vigueur au mois d'effet choisi : cocher
  // ou décocher revient à déclarer une entrée ou une sortie à partir de ce mois.
  const inclusesEffet = useMemo(() => {
    const p = panierAu(moisEffet);
    return new Set([...p.values()].filter((e) => e.inclus).map((e) => e.prestation_id));
  }, [ligne.contenu, moisEffet]);

  useEffect(() => { setSelection(new Set(inclusesEffet)); }, [inclusesEffet]);

  const entrees = [...selection].filter((id) => !inclusesEffet.has(id));
  const sorties = [...inclusesEffet].filter((id) => !selection.has(id));
  const changements = entrees.length + sorties.length;

  const parGroupe = useMemo(() => {
    const groupes = new Map<string, Prestation[]>();
    for (const p of prestations) {
      const g = p.groupe ?? 'Sans groupe';
      if (!groupes.has(g)) groupes.set(g, []);
      groupes.get(g)!.push(p);
    }
    return [...groupes.entries()];
  }, [prestations]);

  const libelle = (id: number) => prestations.find((p) => p.id === id)?.libelle ?? `#${id}`;
  const valide = /^\d{4}-\d{2}$/.test(moisEffet) && changements > 0 && commentaire.trim().length > 0;

  async function enregistrer() {
    if (!valide || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      for (const prestationId of entrees) {
        await api.post(`/contrats/prestations/${ligne.id}/contenu`, {
          prestationId, moisEffet, inclus: true, commentaire: commentaire.trim(),
        });
      }
      for (const prestationId of sorties) {
        await api.post(`/contrats/prestations/${ligne.id}/contenu`, {
          prestationId, moisEffet, inclus: false, commentaire: commentaire.trim(),
        });
      }
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
      setEnCours(false);
    }
  }

  async function supprimer(id: number) {
    const c = window.prompt('Commentaire de suppression (obligatoire) :');
    if (!c || !c.trim()) return;
    setErreur(null);
    try {
      await api.delete(`/contrats/contenu/${id}`, { commentaire: c.trim() });
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal" style={{ maxWidth: 820 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{contratLibelle} — {ligne.libelle}</div>
            <div className="text-xs text-ink-3">
              Panier de prestations : ce que cette ligne facturable recouvre réellement, à une date donnée.
            </div>
          </div>
          <button className="close-btn" onClick={onFermer} title="Fermer (Esc)"><X className="icon-sm" /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto' }}>
          {erreur && <div className="text-err text-sm mb-8">{erreur}</div>}

          <div className="formulaire-changement mb-16">
            <div className="flex items-end gap-12 flex-wrap">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Panier en vigueur en</label>
                <input type="month" className="form-input" value={moisLecture}
                  onChange={(e) => setMoisLecture(e.target.value)} />
              </div>
              <div className="flex gap-4 flex-wrap items-center" style={{ flex: 1 }}>
                {inclusesLecture.size === 0
                  ? <span className="text-sm text-ink-3">Aucune prestation dans le panier à cette date.</span>
                  : [...inclusesLecture].map((id) => (
                      <span key={id} className="badge badge-accent badge-sm"
                        title={`Depuis ${fmtMoisFr(panierLecture.get(id)!.mois_effet)} — ${panierLecture.get(id)!.commentaire}`}>
                        {libelle(id)}
                      </span>
                    ))}
              </div>
            </div>
          </div>

          <div className="formulaire-changement mb-16">
            <div className="formulaire-changement__titre">Composer le panier à partir d'un mois donné</div>
            <div className="flex items-end gap-12 flex-wrap mb-8">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">À partir de</label>
                <input type="month" className="form-input" value={moisEffet}
                  onChange={(e) => setMoisEffet(e.target.value)} />
              </div>
              <span className="text-xs text-ink-3" style={{ paddingBottom: 6 }}>
                Les cases reflètent le panier en vigueur à ce mois — cocher ou décocher crée une entrée ou une
                sortie datée, sans réécrire le passé.
              </span>
            </div>

            {parGroupe.map(([groupe, liste]) => (
              <div key={groupe} className="mb-8">
                <div className="text-xs font-semi text-ink-2">{groupe}</div>
                <div className="flex gap-12 flex-wrap">
                  {liste.map((p) => (
                    <label key={p.id} className="toggle-label" title={p.description ?? undefined}>
                      <span className="toggle">
                        <input type="checkbox" className="toggle-input" checked={selection.has(p.id)}
                          onChange={(e) => {
                            const s = new Set(selection);
                            if (e.target.checked) s.add(p.id); else s.delete(p.id);
                            setSelection(s);
                          }} />
                        <span className="toggle-slider" />
                      </span>
                      <span className="text-sm">{p.libelle}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            {changements > 0 && (
              <p className="text-sm text-ink-2">
                {entrees.length > 0 && <>Entrent dès {moisEffet} : <strong>{entrees.map(libelle).join(', ')}</strong>. </>}
                {sorties.length > 0 && <>Sortent dès {moisEffet} : <strong>{sorties.map(libelle).join(', ')}</strong>.</>}
              </p>
            )}

            <div className="form-group mt-8">
              <label className="form-label">Commentaire (obligatoire)</label>
              <input className="form-input w-full" value={commentaire}
                placeholder="Décision, avenant ou constat qui justifie ce changement de contenu…"
                onChange={(e) => setCommentaire(e.target.value)} />
            </div>
            <div className="flex justify-end">
              <button className="btn btn-accent" disabled={!valide || enCours} onClick={enregistrer}>
                {enCours ? 'Enregistrement…' : `Enregistrer ${changements > 0 ? `(${changements})` : ''}`}
              </button>
            </div>
          </div>

          <div className="text-xs text-ink-3">Historique du contenu</div>
          <table className="table table-compact">
            <thead>
              <tr><th>Dès</th><th>Prestation</th><th>Sens</th><th>Commentaire</th><th style={{ width: 40 }} /></tr>
            </thead>
            <tbody>
              {ligne.contenu.map((e) => (
                <tr key={e.id}>
                  <td className="tabular-nums">{fmtMoisFr(e.mois_effet)}</td>
                  <td>{e.prestation_libelle}</td>
                  <td>{e.inclus
                    ? <span className="badge badge-ok badge-sm">entre</span>
                    : <span className="badge badge-warn badge-sm">sort</span>}
                  </td>
                  <td className="text-sm text-ink-2">{e.commentaire}</td>
                  <td>
                    <button className="btn btn-icon" title="Supprimer cet événement"
                      onClick={() => supprimer(e.id)}>
                      <Trash2 className="icon-sm" />
                    </button>
                  </td>
                </tr>
              ))}
              {ligne.contenu.length === 0 && (
                <tr><td colSpan={5} className="text-ink-3">Aucun contenu déclaré pour cette ligne.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
